import { Injectable, Injector } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, of, from } from 'rxjs';
import { auditTime, switchMap, tap } from 'rxjs/operators';

import { StorageService } from './storage.service';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { Purpose } from '@app/models';

export interface Credentials {
  accessToken: string;
  tokenType: string;
  expiresAt: number;
  mfa: string[];
  mfaAccepted?: boolean;
  firebaseToken?: string;
  purpose?: Purpose;
}

export interface AccessTokenJwtPayload extends JwtPayload {
  email?: string;
  enterprise_id?: number;
  organization_id?: number;
}

export interface CredentialsOptions {
  useSession?: boolean;
  disableChangeTrigger?: boolean;
}

const credentialsKey = 'painel-autentique-credentials';

/**
 * Provides storage for authentication credentials.
 */
@Injectable({
  providedIn: 'root'
})
export class CredentialsService {
  private _credentials: Credentials | null = null;
  private readonly credentialsChange: BehaviorSubject<Credentials>;

  constructor(
    private injector: Injector,
    private storageService: StorageService
  ) {
    this._credentials = this.storageService.getJSON(credentialsKey);
    this.credentialsChange = new BehaviorSubject<Credentials>(this._credentials);
  }

  /**
   * Checks if the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials && new Date() < new Date(this.credentials.expiresAt);
  }

  /**
   * Checks if the user is authenticated, and either does not have 2FA setup, or they do and is active on current session.
   * @return True if the conditions are met
   */
  isTwoStepAuthenticated(): boolean {
    return this.isAuthenticated() && (this.credentials.mfa.length === 0 || !!this.credentials.mfaAccepted);
  }

  /**
   * Checks if the user is authenticated, but did not accept 2FA yet.
   * @return True if the conditions are met
   */
  isMissingTwoStep(): boolean {
    return this.isAuthenticated() && !this.isTwoStepAuthenticated();
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    if (this._credentials) {
      // Fix for old sessions, that may not have mfa property
      this._credentials.mfa = this._credentials.mfa || [];
    }
    return this._credentials;
  }

  /**
   * Gets the user credentials' header.
   * @return The user credentials' header or null if the user is not authenticated.
   */
  get authorizationHeader() {
    return this._credentials ? { Authorization: `${this._credentials.tokenType} ${this._credentials.accessToken}` } : null;
  }

  get decodedAccessToken() {
    return this._credentials?.accessToken ? this.decodeJwt(this._credentials.accessToken) : null;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param options Extra options for setting the user credentials.
   */
  setCredentials(credentials?: Credentials, options: CredentialsOptions = {}) {
    this._credentials = credentials || null;

    if (credentials) {
      credentials.mfa = credentials.mfa || []; // Fix for old sessions, that may not have mfa property
      this._credentials = credentials;
      this.storageService.setJSON(credentialsKey, credentials, options.useSession);
    } else {
      this.storageService.remove(credentialsKey);
    }

    if (!options.disableChangeTrigger && (!credentials || credentials.mfaAccepted || credentials.mfa.length === 0)) {
      this.credentialsChange.next(credentials);
    }
  }

  /**
   * Clears the user credentials.
   */
  clearCredentials() {
    this.setCredentials();
    this.storageService.remove('painel-autentique-firestore-credentials');
    setTimeout(() => this.apolloCacheClear().subscribe());
  }

  apolloCacheClear() {
    return of(this.injector.get(Apollo)).pipe(
      tap(apollo => apollo.client.stop()),
      switchMap(apollo => from(apollo.client.clearStore()))
    );
  }

  onChangeCredentials() {
    return this.credentialsChange.asObservable().pipe(
      auditTime(100) // Prevents repeated emissions by holding out 100ms and returning the latest value
    );
  }

  decodeJwt(value: any) {
    return jwtDecode(value) as AccessTokenJwtPayload;
  }
}
